"use client";

import { c as _c } from "react/compiler-runtime";
import type { FC } from "react";
import { useCallback, useMemo } from "react";
import Image from "next/image";
import { AnimatePresence, motion } from "motion/react";
import type { FlagAspectRatioUnion } from "@d0paminedriven/iso-3166-1";
import { shimmer } from "@/lib/shimmer";
import { cn } from "@/lib/utils";
import { UsersIcon } from "@/ui/svg/users";
interface WorldCountryFlagCountsProps {
  flagUrl: string;
  countryName: string;
  visitors: string | number;
  flagAspectRatio: FlagAspectRatioUnion;
}
type InferAspectRatioSplit<T> = T extends `${infer X}/${infer Y}` ? [X, Y] : never;
const flagWidth = 72;
export const WorldCountryFlagCounts: FC<WorldCountryFlagCountsProps> = t0 => {
  const $ = _c(38);
  const {
    countryName,
    flagUrl,
    visitors,
    flagAspectRatio
  } = t0;
  const aspectClassName = `aspect-[${flagAspectRatio}]`;
  const aspectRatioHelper = _temp;
  const aspectRatio = aspectRatioHelper(flagAspectRatio);
  const flagHeight = flagWidth / aspectRatio;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      opacity: 0,
      y: 20
    };
    t2 = {
      opacity: 1,
      y: 0
    };
    t3 = {
      opacity: 0,
      y: -20
    };
    t4 = {
      duration: 0.5
    };
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
    $[3] = t4;
  } else {
    t1 = $[0];
    t2 = $[1];
    t3 = $[2];
    t4 = $[3];
  }
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = cn("h-auto w-18 shrink-0 overflow-hidden");
    t6 = {
      scale: 0.8,
      opacity: 0
    };
    t7 = {
      scale: 1,
      opacity: 1
    };
    t8 = {
      duration: 0.5,
      delay: 0.2
    };
    $[4] = t5;
    $[5] = t6;
    $[6] = t7;
    $[7] = t8;
  } else {
    t5 = $[4];
    t6 = $[5];
    t7 = $[6];
    t8 = $[7];
  }
  const t9 = flagUrl ?? "/en.svg";
  const t10 = `Flag of ${countryName}`;
  let t11;
  if ($[8] !== flagHeight) {
    t11 = shimmer([flagWidth, flagHeight]);
    $[8] = flagHeight;
    $[9] = t11;
  } else {
    t11 = $[9];
  }
  let t12;
  if ($[10] !== aspectClassName) {
    t12 = cn("h-auto w-full object-cover", aspectClassName);
    $[10] = aspectClassName;
    $[11] = t12;
  } else {
    t12 = $[11];
  }
  let t13;
  if ($[12] !== flagHeight || $[13] !== t10 || $[14] !== t11 || $[15] !== t12 || $[16] !== t9) {
    t13 = <motion.div className={t5} initial={t6} animate={t7} transition={t8}><Image src={t9} alt={t10} width={flagWidth} height={flagHeight} placeholder="blur" loading="eager" blurDataURL={t11} className={t12} priority={true} /></motion.div>;
    $[12] = flagHeight;
    $[13] = t10;
    $[14] = t11;
    $[15] = t12;
    $[16] = t9;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  let t14;
  let t15;
  let t16;
  let t17;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      opacity: 0,
      y: 10
    };
    t15 = {
      opacity: 1,
      y: 0
    };
    t16 = {
      opacity: 0,
      y: -10
    };
    t17 = {
      duration: 0.3
    };
    $[18] = t14;
    $[19] = t15;
    $[20] = t16;
    $[21] = t17;
  } else {
    t14 = $[18];
    t15 = $[19];
    t16 = $[20];
    t17 = $[21];
  }
  const t18 = /^(United Kingdom of Great Britain and Northern Ireland)$/g.test(countryName) === true ? "United Kingdom" : countryName;
  let t19;
  if ($[22] !== countryName || $[23] !== t18) {
    t19 = <AnimatePresence mode="wait"><motion.p key={countryName} className="text-base font-medium text-white sm:text-lg" initial={t14} animate={t15} exit={t16} transition={t17}>{t18}</motion.p></AnimatePresence>;
    $[22] = countryName;
    $[23] = t18;
    $[24] = t19;
  } else {
    t19 = $[24];
  }
  let t20;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <UsersIcon className="size-4 text-white sm:size-5" />;
    $[25] = t20;
  } else {
    t20 = $[25];
  }
  let t21;
  let t22;
  let t23;
  let t24;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = {
      opacity: 0,
      y: 10
    };
    t22 = {
      opacity: 1,
      y: 0
    };
    t23 = {
      opacity: 0,
      y: -10
    };
    t24 = {
      duration: 0.3
    };
    $[26] = t21;
    $[27] = t22;
    $[28] = t23;
    $[29] = t24;
  } else {
    t21 = $[26];
    t22 = $[27];
    t23 = $[28];
    t24 = $[29];
  }
  let t25;
  if ($[30] !== visitors) {
    t25 = <div className="flex flex-row items-center space-x-1 sm:space-x-1.5">{t20}<AnimatePresence mode="wait"><motion.p key={visitors} className="text-base text-white" initial={t21} animate={t22} exit={t23} transition={t24}>{visitors}</motion.p></AnimatePresence></div>;
    $[30] = visitors;
    $[31] = t25;
  } else {
    t25 = $[31];
  }
  let t26;
  if ($[32] !== t19 || $[33] !== t25) {
    t26 = <div className="flex flex-col space-y-0.5 whitespace-nowrap sm:space-y-1">{t19}{t25}</div>;
    $[32] = t19;
    $[33] = t25;
    $[34] = t26;
  } else {
    t26 = $[34];
  }
  let t27;
  if ($[35] !== t13 || $[36] !== t26) {
    t27 = <motion.div className="container flex max-w-3xl items-center space-x-3" initial={t1} animate={t2} exit={t3} transition={t4}>{t13}{t26}</motion.div>;
    $[35] = t13;
    $[36] = t26;
    $[37] = t27;
  } else {
    t27 = $[37];
  }
  return t27;
};
function _temp(flagAspectRatio_0) {
  const [width, height] = flagAspectRatio_0.split("/") as InferAspectRatioSplit<FlagAspectRatioUnion>;
  return /\./g.test(width) === false ? Number.parseInt(width, 10) / Number.parseInt(height, 10) : Number.parseFloat(width) / Number.parseInt(height, 10);
}